import { GetterTree, MutationTree, ActionTree } from 'vuex'
import * as T from '@/types'
import api from '@/api'

import router from '@/router'
import config from '@/config'
import JSONAPIDeserializer from 'ts-jsonapi'

const deserializer = new JSONAPIDeserializer.Deserializer({keyForAttribute: 'camelCase'})

const INITIAL_STATE: () => T.Store.AuthState = () => ({
    loggingIn: false,
    loggedIn: null,
    initialised: false,

    resolvingRoute: false,

    token: null,

    rememberEmail: false,
    rememberedEmail: null,

    hasAccount: false,

    me: null,
})

const getters: GetterTree<T.Store.AuthState, T.Store.RootState> = {
    loggingIn: (state) => state.loggingIn,
    loggedIn: (state) => state.loggedIn,
    initialised: (state) => state.initialised,

    resolvingRoute: (state) => state.resolvingRoute,

    token: (state) => state.token,

    rememberEmail: (state) => state.rememberEmail,
    rememberedEmail: (state) => state.rememberedEmail && atob(state.rememberedEmail),

    hasAccount: (state) => state.hasAccount,

    me: (state) => state.me,
}

const mutations: MutationTree<T.Store.AuthState> = {

    beginLoggingIn (state) {
        state.loggingIn = true
    },

    finishLoggingIn (state) {
        state.loggingIn = false
        state.loggedIn = true
    },

    setLoggedIn (state, loggedIn) {
        state.loggedIn = loggedIn
    },

    setLoggingIn (state, loggingIn) {
        state.loggingIn = loggingIn
    },

    setInitialised (state, initialised) {
        state.initialised = initialised
    },

    setResolvingRoute (state, resolvingRoute) {
        state.resolvingRoute = resolvingRoute
    },

    setToken: (state, token) => state.token = token,

    setRememberEmail: (state, remember) => state.rememberEmail = remember,

    setRememberedEmail: (state, email) => {
        state.rememberedEmail = email && btoa(email)
    },

    setHasAccount: (state, hasAccount) => {
        state.hasAccount = hasAccount
    },

    setMe: (state, me) => {
        state.me = me
    },

}

const actions: ActionTree<T.Store.AuthState, T.Store.RootState> = {

    ////////////////////////////////////////////////////////////////
    // login, initialisation, & logout
    ////////////////////////////////////////////////////////////////

    async login ({ commit, dispatch, getters }, { email, password, shouldRedirect }) {
        commit('beginLoggingIn')

        let response
        try {
            response = await api.auth.login(email, password)
        }
        catch ({ response }) {
            commit('finishLoggingIn')
            if(!response) {
                throw 'An error has occurred while trying to log in. Please try again.'
            }

            throw response
        }

        if (response.data.showFirstLogin){
            console.log('First login')
            commit('setLoggingIn', false)
            return response
        } 

        const { token } = response.data
        commit('setToken', token)
        commit('setRememberedEmail', getters.rememberEmail ? email : null)

        await dispatch('initialise')

        if (shouldRedirect) {
            router.push({ name: config.ROUTE.DEFAULT_AUTHORIZED_ROUTE_NAME })
        } 
        
        commit('finishLoggingIn')

        return response
    },

    async setToken ({ commit, dispatch, getters }, token) {
        commit('setToken', token)
    },

    async loadMe ({ commit }) {
        let response, me
        try {
            response = await api.auth.me()
            me = deserializer.deserialize(response.data)
        } catch (error) {
            console.log('Error loading me:', {error})
            return
        }
        if (!response) throw ('Error loading me')
        commit('setMe', me)
    },    

    async loadAccountWideEntities ({ dispatch, rootGetters }) {
        await Promise.all([
            dispatch('creditors/refresh', null, { root: true }),
            dispatch('sources/refresh', null, { root: true }),
            dispatch('tags/refresh', null, { root: true }),
        ])
    },
    
    async initialise ({ commit, dispatch }) {
        console.group(`initialise`)
        
        ////////////////////////////////////////////////////////////////
        // Load me.
        ////////////////////////////////////////////////////////////////

        await Promise.all([
            dispatch('auth/loadMe', null, { root: true }),
        ])

        ////////////////////////////////////////////////////////////////
        // Load everything else.
        ////////////////////////////////////////////////////////////////

        try {
            await Promise.all([
                dispatch('loadAccountWideEntities'),
            ])
        }
        catch (error) {
            if (error.response.status === 402) {
                commit('setInitialised', true)
            }
            throw error
        }

        ////////////////////////////////////////////////////////////////
        // Done!
        ////////////////////////////////////////////////////////////////

        commit('setInitialised', true)

        console.groupEnd()
    },

    logout ({ commit, dispatch }, options?: { doNotChangeRoute?: boolean }) {
        console.log('Logging out...')
        
        commit('setToken', null)
        commit('setLoggedIn', null)
        commit('setInitialised', false)
        
        if (!options || !options.doNotChangeRoute) {
            console.log(`Redirecting to ${T.CrmRoute.AUTH_LOGIN}`)
            router.push({ name: T.CrmRoute.AUTH_LOGIN })
        }

    },

}

export default {
    namespaced: true,
    state: { ...INITIAL_STATE() },
    getters,
    mutations,
    actions,
}
